import type { PeerParticipant, PeerTeam, PeerTypes } from '../../types';
import type {
  CreatePeerRequest,
  PeerParticipantsWith__Type,
  PeerTeamWith__Type,
  UpdatePeerRequest,
} from './types';

type ConstructRequest<T extends PeerTypes> =
  | Omit<CreatePeerRequest<T>, 'causeId' | 'campaignId'>
  | Omit<UpdatePeerRequest<T>, 'causeId' | 'campaignId' | 'uuid'>;

type ConstructPeerForm<T extends PeerTypes> = {
  causeId: string;
  campaignId: string;
  peerType: PeerTypes;
  request: ConstructRequest<T>;
  uuid?: string;
};

const constructPeerFormData = <T extends PeerTypes>({
  causeId,
  campaignId,
  uuid,
  peerType,
  request,
}: ConstructPeerForm<T>) => {
  const form = new FormData();
  const peerKey = peerType === 'participants' ? 'participant' : 'team';
  Object.entries(request).map(([key, value]) => {
    if (key == 'image' && value) {
      form.append(`${peerKey}[image]`, value);
    } else if (key == 'options' && value) {
      Object.entries(value).map(([key]) => {
        form.append(`${peerKey}[options][${key}]`, value[key]);
      });
    } else if (value !== undefined) {
      form.append(`${peerKey}[${key}]`, value);
    }
  });

  form.append('cause_id', causeId);
  form.append('store_item_id', campaignId);
  if (uuid) {
    form.append('uuid', uuid);
  }

  return form;
};

//Below are are going to create our help fn and types that allow us to return a peer with a __type property
type campaignPeerTypes = PeerTeam | PeerParticipant;

const peer__types = {
  'PeerPages::Participant': 'participants',
  'PeerPages::Team': 'teams',
} as const;

type DynamicPeerType<T> = T extends PeerTeam
  ? PeerTeamWith__Type
  : T extends PeerParticipant
    ? PeerParticipantsWith__Type
    : never;

const campaignPeerWithType = <T extends campaignPeerTypes>(peerPage: T): DynamicPeerType<T> => {
  const type = peerPage?.type;
  const peerType = peer__types[type];
  return {
    ...peerPage,
    __type: peerType,
  } as unknown as DynamicPeerType<T>;
};

export { campaignPeerWithType, constructPeerFormData };
